"use strict";

import {Utils} from "@/backoffice/scripts/global.js";

registerModule('flash-messages');

window.triggerFlashMessage = data => {
    if (data.flashMessage && Array.isArray(data.flashMessage)) {
        data.flashMessage.forEach(message => new Snackbar(message))
    }

    if (data['flash-messages']) {
        for (let type in data['flash-messages']) {
            new Snackbar({
                message: data['flash-messages'][type],
                class: type,
            });
        }
    }
};

const SnackbarQueue = {
    /** @type {Snackbar[]} */
    _queue: [],
    /** @type {null|Snackbar} */
    showing: null,
    /**
     * Adds a snackbar to the queue and if possible, show it.
     * @param {Snackbar} snackbar
     */
    queue: function (snackbar) {
        if (this.areAnyShowing()) {
            this._queue.push(snackbar);
        } else {
            snackbar.show();
        }
    },
    /**
     * Returns if there are any snackbars currently visible on-screen.
     * @return {boolean}
     */
    areAnyShowing: function () {
        return !! this.showing;
    },
    /**
     * Attempts to show the next snackbar, if none are currently visible.
     */
    showNext: function () {
        if (this._queue.length > 0 && ! this.areAnyShowing()) {
            const bar = this._queue.shift();
            bar.show();
        } else if (this._queue.length === 0 && ! this.showing) {
            // we've cleared all. Just set the localStorage entry now to avoid any missing beforeUnload calls.
            localStorage.setItem('queued_snackbars', this.toJson());
        }
    },
    /**
     * Converts the queue as well as the currently-showing to json.
     * @return {string}
     */
    toJson: function () {
        const list = this._queue;

        // save the current snackbar, but not if it doesn't auto-hide.
        if (this.areAnyShowing() && this.showing.props.autoHide !== false) {
            list.unshift(this.showing);
        }

        return JSON.stringify(
            list.map(snackbar => snackbar.props)
        );
    },
    /**
     * Takes a json string and parses the items to snackbar instances.
     * @param {string} json
     */
    fromJson: function (json) {
        const data = JSON.parse(json);

        if (Array.isArray(data)) {
            data.forEach(item => new Snackbar(item));
        }
    },
};

// Saves the queue to localStorage when the page is unloaded.
on('beforeunload', () => {
    if (window.localStorage) {
        localStorage.setItem('queued_snackbars', SnackbarQueue.toJson());
    }
});
// Restores the original queue when the page is loaded.
onLoad(() => {
    if (window.localStorage) {
        const queue = localStorage.getItem('queued_snackbars');

        if (typeof(queue) === 'string') {
            SnackbarQueue.fromJson(queue);
        }
    }
});

export default class Snackbar {
    /**
     *
     * @param {{
     *     message: string,
     *     autoHide?: boolean,
     *     duration?: number,
     *     class?: string,
     * }} props
     */
    constructor(props) {
        props.duration = props.duration || 5000;

        this.props = props;
        this.hideTimeout = 0;

        this.build();
        SnackbarQueue.queue(this);
    }

    build() {
        this.element = Utils.getFragment('snackbar-template');

        if (this.props.message ) {
            this.element.querySelector('.snackbar-message')
                .innerHTML = this.props.message;
        }

        this.element.addEventListener('mouseover', this.cancelTimer.bind(this));
        this.element.addEventListener('touchstart', this.cancelTimer.bind(this));
        this.element.addEventListener('mouseleave', this.restartTimer.bind(this));

        this.element.querySelector('.snackbar-close')
            .addEventListener('click', this.hide.bind(this));

        this.element.style.setProperty('--message-duration', this.props.duration + 'ms');
        if (this.props.class) {
            this.element.classList.add(this.props.class);
        }

        this.timeBar = this.element.querySelector('.snackbar-time-indication');
    }

    cancelTimer() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }

        this.timeBar.classList.add('away');
    }

    restartTimer() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = 0;

            this.timeBar.classList.remove('active');
            this.element.style.setProperty('--message-duration', '0ms');

            requestAnimationFrame(() => requestAnimationFrame(() => {
                this.element.style.setProperty('--message-duration', this.props.duration + 'ms');
                this.hideTimeout = setTimeout(this.hide.bind(this), this.props.duration);
                this.timeBar.classList.remove('away');
                this.timeBar.classList.add('active');
            }));
        }
    }

    show() {
        if (! this.element.parentElement) {
            document.querySelector('.snackbar-wrapper')
                .append(this.element);
        }

        SnackbarQueue.showing = this;
        this.element.classList.add('visible');

        if (this.props.autoHide !== false) {
            this.hideTimeout = setTimeout(this.hide.bind(this), this.props.duration);
            this.timeBar.offsetWidth;
            this.timeBar.classList.add('active');
        } else {
            this.timeBar.classList.add('away');
        }
    }

    hide() {
        this.element.classList.add('hiding');
        this.timeBar.classList.add('away');
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }

        setTimeout(() => {
            this.element.classList.remove('hiding', 'visible');
            this.timeBar.classList.remove('away');

            if (SnackbarQueue.showing === this) {
                SnackbarQueue.showing = null;
            }
            if (this.element.parentElement) {
                this.element.parentElement.removeChild(this.element);
            }

            SnackbarQueue.showNext();
        }, 250);
    }
}

window.Snackbar = Snackbar;
/*
// Testing messages.
on('DOMContentLoaded', () => {
    const story = `This was a triumph!
    I'm making a note here: Huge Success!
    It's hard to overstate my satisfaction.
    Aperture Science
    "We do what we must, because, we can!"
    "For the good of all of us"...
    ...except the ones who are dead.
    Now there's no sense crying over every mistake.
    You just keep on trying until you run out of cake.
    And the science gets done,
    and you make a neat gun.
    For the people who are still alive!`;

    story.split("\n").forEach((fragment, index) => {
        new Snackbar({ message: fragment, autoHide: index > 0 });
    })
})
*/

